import React, { Component } from 'react'
import PropTypes from 'prop-types'
import IPropTypes from 'react-immutable-proptypes'
import { FormattedMessage } from 'react-intl'
import _ from 'lodash'
import cx from 'classnames'
import ReactResizeDetector from 'react-resize-detector'
import Tooltip from 'gnos-tooltip'
import { animate } from 'gnos-utilities/animation'

import config from '../../../../../../config'

import Column from './components/Column'
import ConnectButton from './components/ConnectButton'
import DummyButton from '../../../../../../components/Button'

import Separator from '../Separator'

import ControlButton from './components/ControlButton'

import hocOnChange from '../HocOnChange'
import hocConnect from './hocConnect'

import './style.scss'

@hocOnChange
@hocConnect
export default class ButtonsArray extends Component {
    static propTypes = {
        animation: PropTypes.bool,
        columnSize: PropTypes.number,
        configuration: IPropTypes.list.isRequired,
        maxPosition: PropTypes.number.isRequired,
        moduleName: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        onChange: PropTypes.func,
        position: PropTypes.number.isRequired,
        scrollingDelta: PropTypes.number,
        setPosition: PropTypes.func.isRequired,
        stretch: PropTypes.bool,
    }

    static defaultProps = {
        animation: true,
        columnSize: 3,
        onChange: () => {},
        scrollingDelta: 100,
        stretch: true,
    }

    componentDidMount() {
        const { position } = this.props

        this.setPositionOfButtonArray(position)
    }

    componentDidUpdate(prevProps) {
        const { configuration, position } = this.props

        // the only possibility set position after data loaded async
        if (!prevProps.configuration.equals(configuration)) {
            this.setPositionOfButtonArray(position)
        }
    }

    onResizeHandler = () => {
        if (this._buttons) this.setPositionOfButtonArray(this._buttons.scrollLeft)
    }

    onControlClickHandler = (type) => {
        const { scrollingDelta } = this.props

        const delta = type === 'left' ? -scrollingDelta : scrollingDelta

        const newPosition = this._buttons.scrollLeft + delta

        this.setPositionOfButtonArray(newPosition, false)
    }

    setPositionOfButtonArray = (position = 0, noAnimate = true) => {
        const { animation, moduleName, name, setPosition } = this.props

        const maxPosition = (this._buttons && this._buttons.scrollWidth - this._buttons.clientWidth) || 0
        const boundPosition = _.clamp(position, 0, maxPosition)

        if (!noAnimate && animation) {
            animate(this._buttons, 'scrollLeft', boundPosition, 150, () => {
                setPosition(moduleName, name, boundPosition, maxPosition)
            })
        } else {
            this._buttons.scrollLeft = boundPosition
            setPosition(moduleName, name, boundPosition, maxPosition)
        }
    }

    renderHelpComponent = (textIdPrefix, buttonName) => {
        const { moduleName, name } = this.props

        return (
            <div>
                <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>
                    <FormattedMessage id={`${textIdPrefix}.${moduleName}.${name}.${buttonName}`} />
                </div>
                <div>
                    <FormattedMessage id={`${textIdPrefix}.${moduleName}.${name}.${buttonName}.text`} />
                </div>
            </div>
        )
    }

    renderButtons = () => {
        const { columnSize, configuration, moduleName, name, onChange } = this.props

        // Filter out bisnode certificates button as we want it to be always active and hidden from user
        // so that he can't turn the block/button off
        const filteredConfig = configuration.filter(
            (btn) => btn.get('name') !== config.modules.report.blocks.bisnodeCertificates.key
        )

        const columnCount = _.ceil(filteredConfig.size / columnSize)

        // create array of columns with buttons in
        const buttonsArray = _.map(Array(columnCount), (value, index) => {
            const actPosition = index * columnSize

            let buttons = filteredConfig.slice(actPosition, actPosition + columnSize).map((button) => {
                const buttonName = button.get('name')
                const isButtonDisabled = button.get('disabled')
                const textIdPrefix = 'scenes.Gnosus.components.Ribbon.components.ButtonsArray'

                const text = <FormattedMessage id={`${textIdPrefix}.${moduleName}.${name}.${buttonName}`} />

                const buttonComponent = !isButtonDisabled ? (
                    <ConnectButton
                        moduleName={moduleName}
                        arrayName={name}
                        name={buttonName}
                        text={text}
                        onChange={onChange}
                    />
                ) : (
                    <DummyButton key={buttonName} text={text} active={false} disable />
                )

                return (
                    <Tooltip
                        key={buttonName}
                        id={`tooltip_ribbon_button_${name}_${buttonName}`}
                        content={this.renderHelpComponent(textIdPrefix, buttonName)}
                        width={'200px'}
                    >
                        <div>{buttonComponent}</div>
                    </Tooltip>
                )
            })

            // because of css layout, create dummy buttons elements
            if (buttons.size !== columnSize) {
                const countOfDummyButtons = columnSize - buttons.size
                const dummyButtonsArray = _.map(Array(countOfDummyButtons), (dummyButton, indexDummy) => (
                    <DummyButton key={`dummy_${indexDummy}`} text={'dummy'} active={false} hidden />
                ))

                buttons = buttons.concat(dummyButtonsArray)
            }

            return <Column key={index}>{buttons}</Column>
        })

        return (
            <div className={'buttons-wrap'} ref={(r) => (this._buttons = r)}>
                <div className={'buttons'}>{buttonsArray}</div>
            </div>
        )
    }

    renderScrollControls = () => {
        const { position, maxPosition } = this.props

        const leftActive = position > 0 && maxPosition > 0
        const rightActive = position < maxPosition

        return (
            <div className={'controls-wrap'}>
                <Separator />
                <div className={'controls'}>
                    <ControlButton type={'right'} active={rightActive} onClickHandler={this.onControlClickHandler} />
                    <ControlButton type={'left'} active={leftActive} onClickHandler={this.onControlClickHandler} />
                </div>
            </div>
        )
    }

    render() {
        const { stretch } = this.props

        return (
            <div className={cx('buttons-array', stretch && 'stretch')}>
                <ReactResizeDetector
                    handleWidth
                    onResize={this.onResizeHandler}
                    refreshMode={'debounce'}
                    refreshRate={200}
                />
                {this.renderButtons()}
                {this.renderScrollControls()}
            </div>
        )
    }
}
